import React, { Fragment } from 'react';
import { CreativePreview } from 'components/CreativePreview/CreativePreview';
import { DefaultCreativePreviewModel } from 'components/CreativePreview/CreativePreviewModel';
import {
  faPencilAlt,
  faEye,
  faTrashAlt,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import styles from './creativeList.module.scss';
import i18n from 'i18next';
import _ from 'lodash';
import { CreativeListModel } from 'containers/CreativeList/CreativeListModel';
import { Link } from 'react-router-dom';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { hasFuncs, inCondition } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import TableRowToolBar from 'containers/TableRowToolBar/TableRowToolBar';
import { Status } from 'components/Status/Status';
import { CreativeReviews } from 'components/CreativeReview/CreativeReviews';
import {
  ArchivedNotice,
  ArchivedPreview
} from 'components/ArchivedCreative/ArchivedCreative';
import { LimitationTableCell } from 'components/LimitationTableCell/LimitationTableCell';
import {
  CreativeStatus,
  isOneForAllCreativeType,
  isOutdoor,
  OutdoorType
} from 'core/creative/Creative';
import { UnavailablePreview } from 'components/UnavailablePreview/UnavailablePreview';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import {
  getCompositeSettingInvalidHint,
  isCompositeSettingBlocked
} from 'core/channelGroup/compositeSettingGate';
import {
  formatEpaperDuration,
  getOutdoorTypeLabel,
  usesEpaperDurationFormat
} from 'core/channelGroup/EpaperChannelGroup';

/**
 * 這筆素材屬於哪個銷售渠道。列表只帶素材的類型值，渠道 id 要繞 creativeTypeSettings
 * 查回來。
 */
export const getChannelGroupIdOfCreative = (creative: any) =>
  ChannelGroupSetting.creativeTypeSettings().get(
    _.get(creative, 'creativeType')
  )?.channelGroupId;

export const getCreativeListFormatters = (model: CreativeListModel) => {
  const creativeWritePermission = hasFuncs(Permission.CREATIVE_WRITE);

  const nameIdFormatter = (nameIdData: any, creativeData: any) => {
    if (creativeData.id === 0) {
      return (
        <div>
          {i18n.t('creativeList.labels.nameIdSummary', {
            count: nameIdData
          })}
        </div>
      );
    }
    const handleOnSelect = (_e) => {
      model.handleOnSelect(nameIdData.id);
    };
    const renderLabel = () => (
      <span className={styles.creativeName}>{nameIdData.name}</span>
    );
    const archived = !!creativeData.archived;
    const readOnly =
      archived || isOneForAllCreativeType(creativeData.creativeType);
    return (
      <div className={styles.creativeCell}>
        <div className={styles.selectCheckBox}>
          <PermissionChecker permissionAware={creativeWritePermission}>
            <input
              type='checkbox'
              checked={creativeData.selected}
              disabled={archived}
              onChange={handleOnSelect}
              id={`creativeListInput${nameIdData.id}`}
            />
            <label htmlFor={`creativeListInput${nameIdData.id}`} />
          </PermissionChecker>
        </div>
        <div className={styles.info}>
          {readOnly ? (
            renderLabel()
          ) : (
            <PermissionChecker
              permissionAware={creativeWritePermission}
              renderOtherwise={renderLabel}
            >
              <Link
                to={`/creatives/${nameIdData.id}/edit`}
                className={styles.creativeName}
              >
                {nameIdData.name}
              </Link>
            </PermissionChecker>
          )}
          <div className={styles.creativeID}>{`ID: ${nameIdData.id}`}</div>
          {archived && <ArchivedNotice className={styles.keepColor} />}
        </div>
      </div>
    );
  };

  const nameIdHeaderFormatter = (
    nameIdHeader: any,
    columnIndex: number,
    { sortElement }
  ) => {
    const selectableCount = model.state.viewModelData.filter(
      (row) => !row.archived
    ).length;
    const selectedCount = model.state.selectedCreatives.length;
    return (
      <div className={styles.nameIdHeader}>
        <div className={styles.selectCheckBox}>
          <PermissionChecker permissionAware={creativeWritePermission}>
            <input
              type='checkbox'
              checked={selectableCount > 0 && selectableCount === selectedCount}
              disabled={selectableCount === 0}
              ref={(el) =>
                el &&
                (el.indeterminate =
                  selectableCount !== selectedCount && selectedCount > 0)
              }
              onChange={model.handleOnSelectAll}
              id={'creativeNameIdHeaderFormatter'}
            />
            <label htmlFor='creativeNameIdHeaderFormatter' />
          </PermissionChecker>
        </div>
        {i18n.t(nameIdHeader.text)}
        {sortElement}
      </div>
    );
  };

  const previewFormatter = (previewData: any, creative: any) => {
    if (!previewData) {
      return <div />;
    }
    if (isOneForAllCreativeType(previewData.creativeType)) {
      return (
        <div className={`${styles.creativeCell} ${styles.previewCell}`}>
          <div className={styles.creativeReview}>
            <UnavailablePreview />
          </div>
        </div>
      );
    }
    if (previewData.creativeState === CreativeStatus.ARCHIVED) {
      return (
        <div className={`${styles.creativeCell} ${styles.previewCell}`}>
          <div className={styles.creativeReview}>
            <ArchivedPreview creativeType={previewData.creativeType} />
          </div>
        </div>
      );
    }
    return (
      <div className={`${styles.creativeCell} ${styles.previewCell}`}>
        <div className={styles.creativeReview}>
          <CreativePreview
            model={new DefaultCreativePreviewModel(previewData)}
          />
        </div>
        <IconWithTooltip
          placement='right'
          className={styles.detailBtn}
          onClick={model.showCreativeDetailModal.bind(model, previewData)}
          icon={faEye}
          tooltipProps={{
            id: `creativeListPreviewHint-${creative.id}`,
            tooltip: i18n.t('creativeList.labels.previewHint')
          }}
        />
      </div>
    );
  };

  const textFormatter = (text: any) => {
    if (!text) {
      return <div />;
    }
    return (
      <div className={styles.creativeCell}>
        <div className={styles.text}>{text}</div>
      </div>
    );
  };

  const outdoorTypeFormatter = (outdoorType: any, creative: any) => {
    if (!outdoorType) {
      return <div />;
    }
    const channelGroupId = getChannelGroupIdOfCreative(creative);
    return (
      <div className={styles.creativeCell}>
        <div className={styles.text}>
          {getOutdoorTypeLabel(outdoorType, channelGroupId)}
        </div>
      </div>
    );
  };

  // 時長文字有三種講法：
  // - HTML5：數字直接講秒。
  // - 電子紙的圖片素材：秒數是使用者自訂的，沒有對應的 outdoorForm.labels.durationN
  //   key，照下面的樣板拼會印出 key 本身，所以跟素材摘要頁一樣走 formatEpaperDuration
  //   拆成分與秒。
  // - 其餘：只有 15 / 30 / 0 幾個固定選項，用 key 對照。
  const durationFormatter = (duration: any, creative: any) => {
    if (duration === undefined) {
      return <div />;
    }
    const renderDuration = () => {
      if (creative.outdoorType === OutdoorType.HTML5) {
        return `${duration} ${i18n.t('common.units.seconds')}`;
      }
      if (
        usesEpaperDurationFormat(
          getChannelGroupIdOfCreative(creative),
          creative.outdoorType
        )
      ) {
        return formatEpaperDuration(duration);
      }
      return i18n.t(`outdoorForm.labels.duration${duration}`);
    };
    return (
      <div className={styles.creativeCell}>
        <div className={styles.text}>{renderDuration()}</div>
      </div>
    );
  };

  const ppsLayoutFormatter = (layoutId: any) => {
    if (!layoutId) {
      return <div />;
    }
    const renderLayoutId = () => {
      const realId = layoutId
        .split('(')[0]
        .substring(4)
        .toLowerCase()
        .replace(/-|\s/g, '_');
      const key = `ppsLayoutId.${realId}`;
      return (
        <div key={layoutId}>
          {i18n.exists(key)
            ? i18n.t(`ppsLayoutId.${realId}`)
            : layoutId.split('(')[0].substring(4)}
        </div>
      );
    };
    return (
      <div className={styles.creativeCell}>
        <div className={styles.text}>{renderLayoutId()}</div>
      </div>
    );
  };

  const urlFormatter = (url: any) => {
    if (!url) {
      return <div />;
    }
    return (
      <div className={styles.creativeCell}>
        <a href={url} target='_blank' rel='noopener noreferrer'>
          {url}
        </a>
      </div>
    );
  };

  const limitationFormatter = (limitationData: any, creative: any) => {
    if (!limitationData) {
      return <div />;
    }

    return (
      <LimitationTableCell
        summaryData={limitationData}
        l2ChannelId={creative.id}
      />
    );
  };

  const approvalFormatter = (creativeData: any) => {
    if (!creativeData) {
      return <div />;
    }

    return (
      <div className={styles.keepColor}>
        <CreativeReviews
          creativeData={creativeData}
          reviewCreative={model.reviewCreative.bind(model)}
        />
      </div>
    );
  };

  const streamingFormatter = (streamingSetting: {
    inStream: boolean;
    outStream: boolean;
  }) => {
    if (!streamingSetting) {
      return <div />;
    }
    let content = streamingSetting.inStream
      ? i18n.t('creativeList.labels.inStream')
      : i18n.t('creativeList.labels.outStream');
    if (streamingSetting.inStream && streamingSetting.outStream) {
      content = i18n.t('creativeList.labels.unlimited');
    }
    return (
      <div className={styles.creativeCell}>
        <span style={{ whiteSpace: 'nowrap' }}>{content}</span>
      </div>
    );
  };

  const skipSettingFormatter = (skipSetting) => {
    if (!skipSetting) {
      return <div />;
    }
    const { skippable, nonSkippable, skipOffset } = skipSetting;
    return (
      <div className={styles.creativeCell}>
        {skippable && (
          <>
            {`${i18n.t('creativeList.labels.skippable')} : ${i18n.t('creativeList.labels.skipOffset', { offset: skipOffset })}`}
            <br />
          </>
        )}
        {nonSkippable && (
          <>
            {i18n.t('creativeList.labels.nonSkippable')}
            <br />
          </>
        )}
        {!skippable &&
          !nonSkippable &&
          i18n.t('creativeList.labels.nonSkippable')}
      </div>
    );
  };

  const floatingEditBtnsFormatter = (cell: any, creative: any): any => {
    const archived = !!creative.archived;
    const archivedHint = i18n.t('creativeList.labels.archivedActionHint');
    const canNotDeleteCreative =
      archived || _.get(creative, 'bindingCampaign', []).length > 0;
    // ECOPD-5722: 渠道的合成設定不可用時，這個素材切出來的東西會是錯的，
    // 所以編輯與複製都不給（刪除不受影響）。
    const compositeSettingBlocked = isCompositeSettingBlocked(
      _.get(creative, 'creativeType')
    );
    const oneForAll = isOneForAllCreativeType(_.get(creative, 'creativeType'));
    const canNotEdit = archived || compositeSettingBlocked;
    const canNotCopy =
      archived || !!creative.processing || compositeSettingBlocked;
    // 封存是素材本身的狀態，比渠道設定更根本，兩個原因同時成立時先講封存。
    const actionHint = (fallback: string) => {
      if (archived) {
        return archivedHint;
      }
      return compositeSettingBlocked
        ? getCompositeSettingInvalidHint()
        : fallback;
    };
    const editIcon = (
      <IconWithTooltip
        disabled={canNotEdit}
        icon={faPencilAlt}
        tooltipProps={{
          id: `creativeListEditTip-${creative.id}`,
          tooltip: actionHint(i18n.t('creativeList.labels.editHint'))
        }}
      />
    );
    const getCopyHint = () =>
      actionHint(
        canNotCopy
          ? i18n.t('creativeList.labels.cannotCloneHint')
          : i18n.t('creativeList.labels.cloneHint')
      );
    const getDeleteHint = () => {
      if (archived) {
        return archivedHint;
      }
      return canNotDeleteCreative
        ? i18n.t('creativeList.labels.cannotDeleteHint')
        : i18n.t('creativeList.labels.deleteHint');
    };
    return (
      <PermissionChecker
        permissionAware={hasFuncs(Permission.CREATIVE_WRITE).or(
          hasFuncs(Permission.CREATIVE_DEL)
        )}
      >
        <TableRowToolBar className={styles.floatingEditArea}>
          {!oneForAll && (
            <PermissionChecker permissionAware={creativeWritePermission}>
              {/* 不給編輯時不要包 <Link> —— 包了的話 disabled 只會讓 icon 變灰，
                  點下去照樣導頁。 */}
              {canNotEdit ? (
                editIcon
              ) : (
                <Link to={`/creatives/${creative.id}/edit`}>{editIcon}</Link>
              )}
              <IconWithTooltip
                disabled={canNotCopy}
                icon={faCopy}
                onClick={model.cloneCreatives.bind(model, [creative.id])}
                tooltipProps={{
                  id: `creativeListCloneTip-${creative.id}`,
                  tooltip: getCopyHint()
                }}
              />
            </PermissionChecker>
          )}
          <IconWithTooltip
            disabled={archived}
            onClick={model.showCreativeBindingStatus.bind(model, creative.id)}
            icon={faEye}
            tooltipProps={{
              id: `creativeListBindHint-${creative.id}`,
              tooltip: archived
                ? archivedHint
                : i18n.t('creativeBindingStatusPage.title')
            }}
          />
          <PermissionChecker
            permissionAware={hasFuncs(Permission.CREATIVE_DEL)}
          >
            <IconWithTooltip
              disabled={canNotDeleteCreative}
              icon={faTrashAlt}
              onClick={model.showDeleteCreativeModal.bind(model, [creative.id])}
              tooltipProps={{
                id: `creativeListDeleteTip-${creative.id}`,
                tooltip: getDeleteHint()
              }}
            />
          </PermissionChecker>
          <PermissionChecker
            permissionAware={inCondition(() =>
              isOutdoor(creative.creativeType)
            )}
          >
            <IconWithTooltip
              disabled={archived}
              icon={faExternalLinkAlt}
              onClick={model.toDemoPage.bind(
                model,
                creative.id,
                creative.createTime
              )}
              tooltipProps={{
                id: `creativeListDemoPageTip-${creative.id}`,
                tooltip: archived
                  ? archivedHint
                  : i18n.t('creativeList.labels.demoPageHint')
              }}
            />
          </PermissionChecker>
        </TableRowToolBar>
      </PermissionChecker>
    );
  };

  const stateFormatter = (status: CreativeStatus) => {
    const labelMap = {
      [CreativeStatus.NO_BINDING]: i18n.t('creative.status.disable'),
      [CreativeStatus.DEACTIVE]: i18n.t('creative.status.disable'),
      [CreativeStatus.ACTIVE]: i18n.t('creative.status.enable'),
      [CreativeStatus.PROCESSING]: i18n.t('creative.status.processing'),
      [CreativeStatus.PROCESSING_FAIL]: i18n.t(
        'creative.status.processingFailed'
      ),
      [CreativeStatus.ARCHIVED]: i18n.t('creative.status.archived')
    };
    const colorMap = {
      [CreativeStatus.NO_BINDING]: 'black',
      [CreativeStatus.DEACTIVE]: 'black',
      [CreativeStatus.ACTIVE]: 'success',
      [CreativeStatus.PROCESSING]: 'warning',
      [CreativeStatus.PROCESSING_FAIL]: 'danger',
      [CreativeStatus.ARCHIVED]: 'dark'
    };
    return (
      <div className={styles.keepColor}>
        <Status label={labelMap[status]} color={colorMap[status]} />
      </div>
    );
  };

  return {
    nameIdFormatter,
    previewFormatter,
    textFormatter,
    urlFormatter,
    limitationFormatter,
    approvalFormatter,
    floatingEditBtnsFormatter,
    nameIdHeaderFormatter,
    streamingFormatter,
    skipSettingFormatter,
    ppsLayoutFormatter,
    outdoorTypeFormatter,
    durationFormatter,
    stateFormatter
  };
};
